import React, { useContext, useState, useEffect, useRef, useCallback } from 'react';
import { AuthContext } from '../AuthContext';
import { useNavigate } from 'react-router-dom';
import {
  firestore, collection, query, where, getDocs, deleteDoc,
  doc, orderBy, addDoc, updateDoc, storage
} from '../firebase-config';
import { ref, uploadBytesResumable, getDownloadURL } from 'firebase/storage';
import { Timestamp, getDoc, setDoc } from 'firebase/firestore';
import { Editor } from '@tinymce/tinymce-react';
import '../styles/AdminDashboard.css';

// ─── Inline Blog Post Form ───────────────────────────────────────────────────

const PostForm = ({ post, loggedInUser, onSaved, onCancel }) => {
  const [title, setTitle]               = useState(post?.title       || '');
  const [content, setContent]           = useState(post?.content     || '');
  const [category, setCategory]         = useState(post?.category    || '');
  const [coverImage, setCoverImage]     = useState(post?.coverImage  || '');
  const [authorName, setAuthorName]     = useState(post?.authorName  || '');
  const [youtubeUrl, setYoutubeUrl]     = useState(post?.youtubeUrl  || '');
  const [galleryImages, setGalleryImages] = useState(post?.galleryImages || []);
  const [uploadProgress, setUploadProgress]           = useState(null);
  const [galleryUploadProgress, setGalleryUploadProgress] = useState(null);
  const [saving, setSaving]             = useState(false);
  const [error, setError]               = useState(null);

  const editorRef         = useRef(null);
  const fileInputRef      = useRef(null);
  const galleryInputRef   = useRef(null);
  const inlineImgInputRef = useRef(null);

  const uploadToStorage = (file, folder = 'post-images') =>
    new Promise((resolve, reject) => {
      const storageRef = ref(storage, `${folder}/${Date.now()}-${file.name}`);
      const task = uploadBytesResumable(storageRef, file);
      task.on('state_changed', null, reject, async () => {
        resolve(await getDownloadURL(task.snapshot.ref));
      });
    });

  const insertImageIntoEditor = (url) => {
    if (editorRef.current) {
      editorRef.current.insertContent(`<img src="${url}" />`);
    }
  };

  const handleInlineImageClick = useCallback(() => inlineImgInputRef.current?.click(), []);

  const handleInlineImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const url = await uploadToStorage(file, 'post-images');
    insertImageIntoEditor(url);
    e.target.value = '';
  };

  const handleCoverUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const storageRef = ref(storage, `cover-images/${Date.now()}-${file.name}`);
    const task = uploadBytesResumable(storageRef, file);
    task.on('state_changed',
      (snap) => setUploadProgress(Math.round((snap.bytesTransferred / snap.totalBytes) * 100)),
      (err) => { console.error(err); setUploadProgress(null); },
      async () => { setCoverImage(await getDownloadURL(task.snapshot.ref)); setUploadProgress(null); }
    );
  };

  const handleGalleryUpload = (e) => {
    const files = Array.from(e.target.files);
    if (!files.length) return;
    files.forEach(file => {
      const storageRef = ref(storage, `gallery-images/${Date.now()}-${file.name}`);
      const task = uploadBytesResumable(storageRef, file);
      task.on('state_changed',
        (snap) => setGalleryUploadProgress(Math.round((snap.bytesTransferred / snap.totalBytes) * 100)),
        (err) => { console.error(err); setGalleryUploadProgress(null); },
        async () => {
          const url = await getDownloadURL(task.snapshot.ref);
          setGalleryImages(prev => [...prev, { url, caption: '' }]);
          setGalleryUploadProgress(null);
        }
      );
    });
    e.target.value = '';
  };

  const updateCaption    = (i, caption) => setGalleryImages(prev => prev.map((img, idx) => idx === i ? { ...img, caption } : img));
  const removeGalleryImg = (i)          => setGalleryImages(prev => prev.filter((_, idx) => idx !== i));

  const getYouTubeId = (url) => {
    if (!url) return null;
    const match = url.match(/(?:youtu\.be\/|youtube\.com\/(?:watch\?v=|embed\/|v\/))([^&?/\s]{11})/);
    return match ? match[1] : null;
  };

  const handleSave = async () => {
    const editorContent = editorRef.current ? editorRef.current.getContent() : content;
    if (!title.trim() || !editorContent.trim() || !category.trim()) {
      setError('Please fill in title, content, and category.');
      return;
    }
    setSaving(true);
    setError(null);
    try {
      const postData = {
        title, content: editorContent, category, coverImage, youtubeUrl, galleryImages,
        authorId:   loggedInUser.uid,
        authorName: authorName || loggedInUser.displayName || loggedInUser.email,
        updatedAt:  Timestamp.now(),
      };

      if (post?.id) {
        await updateDoc(doc(firestore, 'posts', post.id), postData);
      } else {
        await addDoc(collection(firestore, 'posts'), {
          ...postData,
          createdAt: Timestamp.now(),
        });
      }
      onSaved();
    } catch (err) {
      console.error(err);
      setError('Failed to save post. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="post-form">
      <div className="form-group">
        <label htmlFor="pf-title">Title</label>
        <input id="pf-title" type="text" value={title} onChange={e => setTitle(e.target.value)} placeholder="Enter blog post title" />
      </div>

      <div className="form-row">
        <div className="form-group">
          <label htmlFor="pf-category">Category</label>
          <input id="pf-category" type="text" value={category} onChange={e => setCategory(e.target.value)} placeholder="e.g. Theology, Community" />
        </div>
        <div className="form-group">
          <label htmlFor="pf-author">Author Name</label>
          <input id="pf-author" type="text" value={authorName} onChange={e => setAuthorName(e.target.value)} placeholder="Author full name" />
        </div>
      </div>

      {/* Cover image */}
      <div className="form-group">
        <label>Cover Image</label>
        <div className="cover-image-field">
          {coverImage && <img src={coverImage} alt="Cover preview" className="cover-preview" />}
          <div className="cover-image-actions">
            <button type="button" className="upload-btn" onClick={() => fileInputRef.current?.click()}>
              {coverImage ? 'Change cover image' : 'Upload cover image'}
            </button>
            {coverImage && <button type="button" className="remove-btn" onClick={() => setCoverImage('')}>Remove</button>}
            {uploadProgress !== null && <span className="upload-progress">Uploading… {uploadProgress}%</span>}
          </div>
          <input ref={fileInputRef} type="file" accept="image/*" style={{ display: 'none' }} onChange={handleCoverUpload} />
        </div>
      </div>

      <div className="form-group">
        <label>Content</label>
        <Editor
          apiKey="16nd2n35tc4ttll8gn95tptzucifo08um2cckdi6rbntk7ha"
          onInit={(evt, editor) => editorRef.current = editor}
          initialValue={post?.content || ''}
          init={{
            height: 400,
            menubar: false,
            plugins: [
              'advlist', 'autolink', 'lists', 'link', 'image', 'charmap', 'preview',
              'anchor', 'searchreplace', 'visualblocks', 'code', 'fullscreen',
              'insertdatetime', 'media', 'table', 'help', 'wordcount'
            ],
            toolbar: 'undo redo | blocks | bold italic forecolor | alignleft aligncenter ' +
              'alignright alignjustify | bullist numlist outdent indent | image | removeformat | help',
            images_upload_handler: async (blobInfo) => {
              const url = await uploadToStorage(blobInfo.blob(), 'post-images');
              return url;
            },
            content_style: 'body { font-family: Helvetica, Arial, sans-serif; font-size: 14px }',
          }}
          onEditorChange={(newContent) => setContent(newContent)}
        />
        <input ref={inlineImgInputRef} type="file" accept="image/*" style={{ display: 'none' }} onChange={handleInlineImageUpload} />
      </div>

      {/* YouTube */}
      <div className="form-group">
        <label htmlFor="pf-youtube">YouTube Video (optional)</label>
        <input
          id="pf-youtube" type="url"
          placeholder="https://youtu.be/…"
          className="youtube-input"
          value={youtubeUrl}
          onChange={e => setYoutubeUrl(e.target.value)}
        />
        {getYouTubeId(youtubeUrl) && (
          <div className="youtube-preview">
            <iframe src={`https://www.youtube.com/embed/${getYouTubeId(youtubeUrl)}`} title="YouTube preview" allowFullScreen />
          </div>
        )}
      </div>

      {/* Gallery */}
      <div className="form-group">
        <label>Gallery Images (optional)</label>
        <div className="gallery-editor">
          {galleryImages.map((img, i) => (
            <div key={i} className="gallery-editor-item">
              <img src={img.url} alt={`Gallery ${i + 1}`} />
              <input type="text" placeholder="Caption (optional)" value={img.caption} onChange={e => updateCaption(i, e.target.value)} className="gallery-caption-input" />
              <button type="button" className="remove-btn" onClick={() => removeGalleryImg(i)}>Remove</button>
            </div>
          ))}
          <div className="gallery-upload-row">
            <button type="button" className="upload-btn" onClick={() => galleryInputRef.current?.click()}>+ Add image(s)</button>
            {galleryUploadProgress !== null && <span className="upload-progress">Uploading… {galleryUploadProgress}%</span>}
          </div>
          <input ref={galleryInputRef} type="file" accept="image/*" multiple style={{ display: 'none' }} onChange={handleGalleryUpload} />
        </div>
      </div>

      {error && <p className="error-message">{error}</p>}

      <div className="button-group">
        <button onClick={handleSave} disabled={saving}>
          {saving ? 'Saving…' : (post?.id ? 'Update Post' : 'Create Post')}
        </button>
        <button onClick={onCancel} className="cancel-btn">Cancel</button>
      </div>
    </div>
  );
};

// ─── Admin Dashboard ─────────────────────────────────────────────────────────

const AdminDashboard = () => {
  const { loggedInUser } = useContext(AuthContext);
  const navigate = useNavigate();

  const [myPosts, setMyPosts]               = useState([]);
  const [loading, setLoading]               = useState(true);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [editingPostId, setEditingPostId]   = useState(null);
  const [featuredIds, setFeaturedIds]       = useState([]);

  useEffect(() => {
    fetchMyPosts();
    fetchFeatured();
  }, [loggedInUser, navigate]);

  const fetchMyPosts = async () => {
    setLoading(true);
    try {
      const postsQuery = query(
        collection(firestore, 'posts'),
        where('authorId', '==', loggedInUser.uid),
        orderBy('createdAt', 'desc')
      );
      const snapshot = await getDocs(postsQuery);
      setMyPosts(snapshot.docs.map(d => ({ id: d.id, ...d.data() })));
    } catch (err) {
      console.error('Error fetching posts:', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchFeatured = async () => {
    try {
      const snap = await getDoc(doc(firestore, 'siteSettings', 'featured'));
      if (snap.exists()) setFeaturedIds(snap.data().featuredPosts || []);
    } catch (err) {
      console.error('Error fetching featured:', err);
    }
  };

  const toggleFeatured = async (postId) => {
    let updated;
    if (featuredIds.includes(postId)) {
      // Unfeature
      updated = featuredIds.filter(id => id !== postId);
    } else {
      if (featuredIds.length >= 2) {
        alert('You already have 2 featured posts. Remove one first.');
        return;
      }
      updated = [...featuredIds, postId];
    }
    try {
      await setDoc(doc(firestore, 'siteSettings', 'featured'), { featuredPosts: updated });
      setFeaturedIds(updated);
    } catch (err) {
      console.error('Error updating featured posts:', err);
      alert('Failed to update featured posts.');
    }
  };

  const handleDelete = async (postId) => {
    if (!window.confirm('Are you sure you want to delete this blog post?')) return;
    try {
      await deleteDoc(doc(firestore, 'posts', postId));
      setMyPosts(prev => prev.filter(p => p.id !== postId));
      // Also unfeature if it was featured
      if (featuredIds.includes(postId)) {
        const updated = featuredIds.filter(id => id !== postId);
        await setDoc(doc(firestore, 'siteSettings', 'featured'), { featuredPosts: updated });
        setFeaturedIds(updated);
      }
    } catch (err) {
      console.error('Error deleting post:', err);
      alert('Failed to delete post.');
    }
  };

  const handleSaved = () => {
    setShowCreateForm(false);
    setEditingPostId(null);
    fetchMyPosts();
  };

  const formatDate = (timestamp) => {
    if (!timestamp) return '';
    return timestamp.toDate().toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' });
  };

  if (!loggedInUser || loggedInUser.role !== 'Admin') return null;

  return (
    <div className="admin-dashboard">
      <h2>Admin Dashboard</h2>

      <div className="dashboard-section">
        <h3>Sermon Management</h3>
        <button className="create-blog-btn" onClick={() => navigate('/admin/sermon-editor')}>
          Manage Sermons →
        </button>
      </div>

      <div className="dashboard-section">
        <h3>Blog Management</h3>

        {!showCreateForm && (
          <button className="create-blog-btn" onClick={() => { setShowCreateForm(true); setEditingPostId(null); }}>
            + Create New Blog Post
          </button>
        )}

        {showCreateForm && (
          <div className="inline-form-wrapper">
            <h4>New Blog Post</h4>
            <PostForm
              loggedInUser={loggedInUser}
              onSaved={handleSaved}
              onCancel={() => setShowCreateForm(false)}
            />
          </div>
        )}

        <div className="my-posts">
          <h4>My Blog Posts ({myPosts.length})</h4>
          {loading ? (
            <p>Loading your posts…</p>
          ) : myPosts.length > 0 ? (
            <div className="posts-list">
              {myPosts.map(post => (
                <div key={post.id} className="post-item">
                  {editingPostId === post.id ? (
                    <div className="inline-form-wrapper">
                      <h4>Editing: {post.title}</h4>
                      <PostForm
                        post={post}
                        loggedInUser={loggedInUser}
                        onSaved={handleSaved}
                        onCancel={() => setEditingPostId(null)}
                      />
                    </div>
                  ) : (
                    <>
                      <div className="post-info">
                        {post.coverImage && <img src={post.coverImage} alt="Cover" className="post-thumb" />}
                        <div>
                          <h5>{post.title}</h5>
                          <p className="post-meta">
                            <span className="category">{post.category}</span> • {formatDate(post.createdAt)}
                            {post.authorName && <> • {post.authorName}</>}
                          </p>
                          {featuredIds.includes(post.id) && (
                            <span className="featured-badge">⭐ Featured</span>
                          )}
                        </div>
                      </div>
                      <div className="post-actions">
                        <button
                          className={featuredIds.includes(post.id) ? 'unfeature-btn' : 'feature-btn'}
                          onClick={() => toggleFeatured(post.id)}
                          title={featuredIds.includes(post.id) ? 'Remove from sidebar' : 'Feature in sidebar'}
                        >
                          {featuredIds.includes(post.id) ? '★ Unfeature' : '☆ Feature'}
                        </button>
                        <button
                          className="edit-btn"
                          onClick={() => { setEditingPostId(post.id); setShowCreateForm(false); }}
                        >
                          Edit
                        </button>
                        <button className="delete-btn" onClick={() => handleDelete(post.id)}>
                          Delete
                        </button>
                      </div>
                    </>
                  )}
                </div>
              ))}
            </div>
          ) : (
            <p>You haven't created any blog posts yet.</p>
          )}
        </div>
      </div>

      <button className="back-btn" onClick={() => navigate('/')}>Back to Home</button>
    </div>
  );
};

export default AdminDashboard;